import * as React from 'react'
import { sessionStore } from 'utils/storage'
import { saveLocalStorageProductPosition } from '@web/components/prod/analytics'

export interface ScrollRestorationState {
  targetScrollY: number
  targetProductsCount: number
}

/**
 * Initializes scroll restoration state from sessionStorage on mount.
 * Returns the state so it can be used in query `enabled` conditions
 * before the effects hook is called.
 */
export function useScrollRestorationState() {
  const [scrollRestoration, setScrollRestoration] = React.useState<ScrollRestorationState | null>(
    null,
  )

  const tabId = React.useId()
  const SCROLL_STORAGE_KEY = `homepage-scroll-position-${tabId}`
  const PRODUCTS_STORAGE_KEY = `homepage-products-count-${tabId}`

  // Initialize scroll restoration state on mount
  React.useEffect(() => {
    const savedScrollPosition = sessionStore.getString(SCROLL_STORAGE_KEY)
    const savedProductsCount = sessionStore.getString(PRODUCTS_STORAGE_KEY)

    if (savedScrollPosition && savedProductsCount) {
      const targetProductsCount = parseInt(savedProductsCount, 10)
      const targetScrollY = parseInt(savedScrollPosition, 10)

      setScrollRestoration({
        targetScrollY,
        targetProductsCount,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    scrollRestoration,
    setScrollRestoration,
    SCROLL_STORAGE_KEY,
    PRODUCTS_STORAGE_KEY,
  }
}

/**
 * Manages scroll restoration effects: loading more products,
 * clearing on filter change, restoring position, and saving on click.
 */
export function useScrollRestorationEffects({
  scrollRestoration,
  setScrollRestoration,
  SCROLL_STORAGE_KEY,
  PRODUCTS_STORAGE_KEY,
  productsLength,
  hasNextPage,
  isFetching,
  fetchNextPage,
  filtersApplied,
}: {
  scrollRestoration: ScrollRestorationState | null
  setScrollRestoration: React.Dispatch<React.SetStateAction<ScrollRestorationState | null>>
  SCROLL_STORAGE_KEY: string
  PRODUCTS_STORAGE_KEY: string
  productsLength: number
  hasNextPage: boolean
  isFetching: boolean
  fetchNextPage: () => Promise<unknown>
  filtersApplied: boolean
}) {
  const isRestoringRef = React.useRef(scrollRestoration !== null)
  const isFetchingMoreRef = React.useRef(false)

  // Track restoring state when scrollRestoration changes
  React.useEffect(() => {
    if (scrollRestoration) {
      isRestoringRef.current = true
    }
  }, [scrollRestoration])

  // Load more products if needed for scroll restoration
  React.useEffect(() => {
    if (
      scrollRestoration &&
      productsLength < scrollRestoration.targetProductsCount &&
      hasNextPage &&
      !isFetching &&
      !isFetchingMoreRef.current
    ) {
      isFetchingMoreRef.current = true
      fetchNextPage().finally(() => {
        isFetchingMoreRef.current = false
      })
    }
  }, [scrollRestoration, productsLength, hasNextPage, isFetching, fetchNextPage])

  // Clear scroll restoration if filters change
  React.useEffect(() => {
    if (filtersApplied && scrollRestoration) {
      sessionStore.remove(SCROLL_STORAGE_KEY)
      sessionStore.remove(PRODUCTS_STORAGE_KEY)
      isRestoringRef.current = false
      setScrollRestoration(null)
    }
  }, [
    filtersApplied,
    scrollRestoration,
    setScrollRestoration,
    SCROLL_STORAGE_KEY,
    PRODUCTS_STORAGE_KEY,
  ])

  // Restore scroll position once we have enough products
  React.useEffect(() => {
    if (
      scrollRestoration &&
      productsLength >= scrollRestoration.targetProductsCount &&
      !isFetching &&
      isRestoringRef.current
    ) {
      const cleanup = () => {
        sessionStore.remove(SCROLL_STORAGE_KEY)
        sessionStore.remove(PRODUCTS_STORAGE_KEY)
        isRestoringRef.current = false
        setScrollRestoration(null)
      }

      // Use ResizeObserver to detect when the page grows tall enough,
      // then scroll — avoids the read-write-read reflow loop.
      const targetY = scrollRestoration.targetScrollY
      let attempts = 0
      const maxAttempts = 20

      const tryScroll = () => {
        window.scrollTo({ top: targetY, behavior: 'instant' as ScrollBehavior })
      }

      const observer = new ResizeObserver(() => {
        attempts++
        const maxScrollY = document.documentElement.scrollHeight - window.innerHeight
        if (maxScrollY >= targetY - 50 || attempts >= maxAttempts) {
          observer.disconnect()
          // Defer the scroll to the next frame so the resize is fully applied
          requestAnimationFrame(() => {
            tryScroll()
            cleanup()
          })
        }
      })

      // Initial attempt after a short delay for first paint
      setTimeout(() => {
        tryScroll()
        const closeEnough = Math.abs(window.scrollY - targetY) < 50
        if (closeEnough) {
          cleanup()
        } else {
          observer.observe(document.documentElement)
        }
      }, 200)
    }
  }, [
    scrollRestoration,
    productsLength,
    isFetching,
    setScrollRestoration,
    SCROLL_STORAGE_KEY,
    PRODUCTS_STORAGE_KEY,
  ])

  const productOnClick = React.useCallback(
    (productId: string, index: number) => {
      saveLocalStorageProductPosition(productId, index)
      sessionStore.setString(SCROLL_STORAGE_KEY, window.scrollY.toString())
      sessionStore.setString(PRODUCTS_STORAGE_KEY, productsLength.toString())
    },
    [SCROLL_STORAGE_KEY, PRODUCTS_STORAGE_KEY, productsLength],
  )

  return { productOnClick }
}
